/**
 * EmbeddedAssistShell — Global VUKZ Assist (Workspace Shell, C4d-1).
 *
 * ÚNICO chat da aplicação. Evolução C4d-1 (01/09/2026): de drawer overlay
 * para COLUNA persistente do shell em desktop:
 *   - desktop (lg+): coluna fixa à direita; o workspace recua via margem
 *     (ver Layout.js) — o Assist não sobrepõe o conteúdo;
 *   - recolher NÃO desmonta o painel (apenas CSS translate) — composer,
 *     scroll e thread sobrevivem;
 *   - mobile (<lg): mantém comportamento de drawer overlay com backdrop;
 *   - VukzAssistPanel monta na primeira abertura e NUNCA mais desmonta
 *     durante a sessão SPA (mount-once).
 *
 * CANVAS-LAYOUT-2 (02/10/2026): o aside volta a ser SÓ o chat (400px fixo à
 * direita em >=1024; drawer full-screen abaixo). O VUKZ Canvas NÃO mora mais
 * aqui: ele ocupa a área central do Layout (MainWorkspace) substituindo
 * visualmente a rota. Sem tabs Chat/Canvas — são coisas diferentes:
 * chat = conversa permanente; canvas = área de trabalho dessa conversa.
 * Fonte única de largura: --vukz-aside-w (AssistContext → Layout).
 * Default aberto/recolhido: AssistContext (aberto >=1440px, persistido).
 *
 * Mounted once in Layout.js — no per-page chat.
 */
import React, { Suspense, useRef } from 'react';
import { Sparkles, Loader2, PanelRightClose } from 'lucide-react';
import { useAssist } from '../../contexts/AssistContext';
import { useAssistFormBridge } from '../../contexts/AssistFormBridge';

// Lazy: o panel importa react-router-dom v7 (ESM) — lazy mantém o jest do
// CRA feliz em componentes que renderizam o Layout.
const VukzAssistPanel = React.lazy(() => import('./VukzAssistPanel'));

const EmbeddedAssistShell = () => {
  const { isOpen, toggle, close, role, layoutMode } = useAssist();
  const { activeFormId } = useAssistFormBridge();

  // Mount-once: depois que o usuário abriu o Assist uma vez, o painel
  // permanece montado mesmo recolhido (collapse = apresentação, não lifecycle).
  const hasOpenedRef = useRef(isOpen);
  if (isOpen) hasOpenedRef.current = true;
  const panelMounted = hasOpenedRef.current;

  return (
    <>
      {/* ── Floating Toggle Button (Assist recolhido) ─────────────── */}
      {!isOpen && (
        <button
          onClick={toggle}
          data-testid="assist-expand"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg
            flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          style={{ background: 'linear-gradient(135deg, #111827, #374151)' }}
          title="Abrir Assistente VUKZ"
        >
          <Sparkles size={22} className="text-white" />
          {/* Badge: formulário registrado na ponte (modo contextual) */}
          {activeFormId && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400
              border-2 border-white" title="Formulário ativo — o Assist pode preencher" />
          )}
        </button>
      )}

      {/* Backdrop (mobile only, quando aberto) */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={close} />
      )}

      {/* ── Coluna/Drawer do Chat — SEMPRE montado após 1ª abertura ── */}
      {panelMounted && (
        <div
          data-testid="assist-column"
          data-layout-mode={layoutMode}
          aria-hidden={!isOpen}
          className={`fixed right-0 top-0 bottom-0 z-50
            w-full lg:w-[400px]
            bg-white border-l border-gray-200 flex flex-col
            shadow-2xl lg:shadow-none
            transition-transform duration-200 ease-out
            ${isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
        >
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 shrink-0">
            <span className="text-xs font-bold text-gray-500 flex items-center gap-1.5">
              <Sparkles size={13} className="text-amber-500" /> VUKZ Assist
            </span>
            <button
              onClick={close}
              data-testid="assist-collapse"
              className="p-2 rounded-lg hover:bg-gray-100 transition"
              title="Recolher Assist"
            >
              <PanelRightClose size={16} className="text-gray-400" />
            </button>
          </div>

          <div data-testid="assist-surface-chat" className="flex-1 min-h-0 p-2">
            <Suspense fallback={
              <div className="h-full flex items-center justify-center text-gray-400">
                <Loader2 size={20} className="animate-spin" />
              </div>
            }>
              <VukzAssistPanel role={role} sport="swimming" fluid />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

export default EmbeddedAssistShell;
